import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  F24_ORIGIN_LABELS,
  F24_ORIGINS,
  F24_SECTION_LABELS,
  F24_SECTIONS,
  f24Balance,
  type F24Detail,
  type F24Line,
  type F24Origin,
  type F24Read,
  type F24Request,
  type F24Response,
  type F24Section,
  formatMoney,
  fromCents,
  italianToday,
  parseDecimal,
  toCents,
} from '@k-management/shared';
import { type ChangeEvent, useCallback, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest, apiUpload } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { InstalmentDuesPicker } from './InstalmentDuesPicker.js';
import { announceTreasuryChange, movementPath } from './treasury-ui.js';

const keys = {
  all: ['treasury', 'f24'] as const,
  year: (year: number) => ['treasury', 'f24', 'year', year] as const,
  one: (id: string) => ['treasury', 'f24', id] as const,
};

type Draft = {
  dueDate: string;
  origin: F24Origin;
  total: string;
  notes: string;
  lines: F24Line[];
};

const emptyLine = (): F24Line => ({
  section: 'erario',
  code: '',
  reference: null,
  period: null,
  debit: '0.00',
  credit: '0.00',
});

const moneyOf = (value: string) => fromCents(Math.round((parseDecimal(value) ?? 0) * 100));

const bodyOf = (draft: Draft): F24Request => ({
  dueDate: draft.dueDate,
  origin: draft.origin,
  total: moneyOf(draft.total),
  notes: draft.notes.trim() || null,
  lines: draft.lines,
});

/** The form of a delega, new or read from a PDF: its lines must make the total. */
function F24Form({
  title,
  draft: first,
  saving,
  error,
  onSave,
  onClose,
}: {
  title: string;
  draft: Draft;
  saving: boolean;
  error?: string;
  onSave: (body: F24Request) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(first);
  const balance = f24Balance(draft.lines);
  const matches = toCents(balance) === toCents(moneyOf(draft.total));
  const complete = draft.dueDate !== '' && draft.lines.every((line) => line.code.trim() !== '');
  const change = (index: number, next: Partial<F24Line>) =>
    setDraft({
      ...draft,
      lines: draft.lines.map((line, at) => (at === index ? { ...line, ...next } : line)),
    });
  return (
    <ConfirmDialog
      title={title}
      confirmLabel="Salva"
      wide
      busy={saving}
      disabled={!matches || !complete}
      error={error}
      onConfirm={() => onSave(bodyOf(draft))}
      onCancel={onClose}
    >
      <div className="row">
        <Field label="Scadenza">
          <input
            type="date"
            value={draft.dueDate}
            onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
          />
        </Field>
        <Field label="Provenienza">
          <select
            value={draft.origin}
            onChange={(event) => setDraft({ ...draft, origin: event.target.value as F24Origin })}
          >
            {F24_ORIGINS.map((origin) => (
              <option key={origin} value={origin}>
                {F24_ORIGIN_LABELS[origin]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Saldo finale">
          <DecimalInput
            className="amount-input"
            aria-label="Saldo finale"
            decimals={[2, 2]}
            value={draft.total}
            onChange={(total) => setDraft({ ...draft, total })}
          />
        </Field>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Sezione</th>
              <th>Codice</th>
              <th>Riferimento</th>
              <th>Periodo</th>
              <th className="numeric">Debito</th>
              <th className="numeric">Credito</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {draft.lines.map((line, index) => (
              <tr key={index}>
                <td>
                  <select
                    aria-label={`Sezione della riga ${index + 1}`}
                    value={line.section}
                    onChange={(event) =>
                      change(index, { section: event.target.value as F24Section })
                    }
                  >
                    {F24_SECTIONS.map((section) => (
                      <option key={section} value={section}>
                        {F24_SECTION_LABELS[section]}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label={`Codice della riga ${index + 1}`}
                    value={line.code}
                    maxLength={10}
                    size={7}
                    onChange={(event) => change(index, { code: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Riferimento della riga ${index + 1}`}
                    value={line.reference ?? ''}
                    maxLength={40}
                    size={12}
                    onChange={(event) => change(index, { reference: event.target.value || null })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Periodo della riga ${index + 1}`}
                    value={line.period ?? ''}
                    maxLength={20}
                    size={8}
                    onChange={(event) => change(index, { period: event.target.value || null })}
                  />
                </td>
                <td className="numeric">
                  <DecimalInput
                    className="amount-input"
                    aria-label={`Debito della riga ${index + 1}`}
                    decimals={[2, 2]}
                    value={line.debit}
                    onChange={(value) => change(index, { debit: moneyOf(value) })}
                  />
                </td>
                <td className="numeric">
                  <DecimalInput
                    className="amount-input"
                    aria-label={`Credito della riga ${index + 1}`}
                    decimals={[2, 2]}
                    value={line.credit}
                    onChange={(value) => change(index, { credit: moneyOf(value) })}
                  />
                </td>
                <td>
                  {draft.lines.length > 1 && (
                    <button
                      type="button"
                      className="small"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          lines: draft.lines.filter((_, at) => at !== index),
                        })
                      }
                    >
                      Togli
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={() => setDraft({ ...draft, lines: [...draft.lines, emptyLine()] })}
      >
        Aggiungi riga
      </button>
      <p className={`small ${matches ? 'ok' : 'error'}`}>
        Righe {formatMoney(balance)}:{' '}
        {matches ? 'tornano con il saldo finale' : 'non fanno il saldo finale'}.
      </p>
      <Field label="Note (facoltative)">
        <input
          value={draft.notes}
          maxLength={500}
          onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
        />
      </Field>
    </ConfirmDialog>
  );
}

/** Tesoreria › F24 (3.23, ADR-032): the deleghe of a year and the movements with none. */
export function F24Page() {
  usePageTitle('F24');
  const canWrite = useCan('tesoreria.write');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const current = Number(italianToday().slice(0, 4));
  const year = Number(params.get('anno') ?? current);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const list = useQuery({
    queryKey: keys.year(year),
    queryFn: () => apiRequest<F24Response>(`/treasury/f24?year=${year}`),
  });
  const read = useMutation({
    mutationFn: (file: File) => apiUpload<F24Read[]>('/treasury/f24/read', file),
    onSuccess: (pages) =>
      setDrafts(
        pages.map((page) => ({
          dueDate: page.dueDate ?? '',
          origin: 'paghe',
          total: page.total ?? fromCents(toCents(f24Balance(page.lines))),
          notes: '',
          lines: page.lines,
        })),
      ),
  });
  const save = useMutation({
    mutationFn: (body: F24Request) =>
      apiRequest<F24Detail>('/treasury/f24', { method: 'POST', body }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: keys.all });
      announceTreasuryChange();
      const rest = drafts.slice(1);
      setDrafts(rest);
      if (rest.length === 0 && drafts.length === 1) void navigate(`/tesoreria/f24/${created.id}`);
    },
  });
  const link = useMutation({
    mutationFn: ({ id, movementId }: { id: string; movementId: string }) =>
      apiRequest<F24Detail>(`/treasury/f24/${id}/movement`, {
        method: 'PUT',
        body: { movementId },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      announceTreasuryChange();
    },
  });
  const years = Array.from({ length: current - 2022 }, (_, index) => current - index);
  const items = list.data?.items ?? [];
  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) read.mutate(file);
  };
  const paid = fromCents(items.reduce((sum, item) => sum + toCents(item.total), 0));

  return (
    <section className="stack">
      <div className="row between">
        <label>
          Anno{' '}
          <select value={year} onChange={(event) => setParams({ anno: event.target.value })}>
            {years.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
        {canWrite && (
          <div className="row">
            <label className="button">
              {read.isPending ? 'Lettura…' : 'Carica PDF'}
              <input type="file" accept="application/pdf" hidden onChange={pick} />
            </label>
            <button
              type="button"
              className="primary"
              onClick={() =>
                setDrafts([
                  {
                    dueDate: '',
                    origin: 'studio',
                    total: '0.00',
                    notes: '',
                    lines: [emptyLine()],
                  },
                ])
              }
            >
              Nuovo F24
            </button>
          </div>
        )}
      </div>
      {read.error && <p className="error">{read.error.message}</p>}
      {list.error && <p className="error">{list.error.message}</p>}
      {link.error && <p className="error">{link.error.message}</p>}
      <div className="kpi-row">
        <div className="card kpi">
          <span className="muted small">Deleghe {year}</span>
          <strong>{items.length}</strong>
          <span className="muted small">{formatMoney(paid)}</span>
        </div>
        <div className="card kpi">
          <span className="muted small">Senza movimento</span>
          <strong>{items.filter((item) => !item.movement).length}</strong>
        </div>
        <div className="card kpi">
          <span className="muted small">Movimenti senza F24</span>
          <strong>{list.data?.movements.length ?? '—'}</strong>
        </div>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Scadenza</th>
              <th>Provenienza</th>
              <th className="numeric">Righe</th>
              <th className="numeric">Totale</th>
              <th>Movimento</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  <Link to={`/tesoreria/f24/${item.id}`}>{formatDate(item.dueDate)}</Link>
                </td>
                <td>{F24_ORIGIN_LABELS[item.origin]}</td>
                <td className="numeric">{item.lines}</td>
                <td className="numeric">{formatMoney(item.total)}</td>
                <td>
                  {item.movement ? (
                    <Link to={movementPath(item.movement.accountCode, item.movement.id)}>
                      {item.movement.accountCode} del {formatDate(item.movement.bookingDate)}
                    </Link>
                  ) : item.candidates.length > 0 && canWrite ? (
                    item.candidates.map((movement) => (
                      <button
                        key={movement.id}
                        type="button"
                        className="small"
                        disabled={link.isPending}
                        onClick={() => link.mutate({ id: item.id, movementId: movement.id })}
                      >
                        Collega {movement.accountCode} del {formatDate(movement.bookingDate)} ·{' '}
                        {formatMoney(movement.amount)}
                      </button>
                    ))
                  ) : (
                    <span className="pill warn">da collegare</span>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Nessuna delega nel {year}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {(list.data?.movements.length ?? 0) > 0 && (
        <>
          <h3>Movimenti di banca senza F24</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Conto</th>
                  <th className="numeric">Importo</th>
                  <th>Descrizione</th>
                </tr>
              </thead>
              <tbody>
                {list.data!.movements.map((movement) => (
                  <tr key={movement.id}>
                    <td>
                      <Link to={movementPath(movement.accountCode, movement.id)}>
                        {formatDate(movement.bookingDate)}
                      </Link>
                    </td>
                    <td>{movement.accountCode}</td>
                    <td className="numeric">{formatMoney(movement.amount)}</td>
                    <td className="small">{movement.description.slice(0, 90)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {drafts[0] && (
        <F24Form
          key={drafts.length}
          title={drafts.length > 1 ? `Nuovo F24 · ne restano ${drafts.length}` : 'Nuovo F24'}
          draft={drafts[0]}
          saving={save.isPending}
          error={save.error?.message}
          onSave={(body) => save.mutate(body)}
          onClose={() => setDrafts([])}
        />
      )}
    </section>
  );
}

/** A delega with its lines, its movement and the instalments it pays. */
export function F24DeliveryPage() {
  const { id = '' } = useParams();
  const canWrite = useCan('tesoreria.write');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dues, setDues] = useState<string[]>([]);
  const chooseDues = useCallback((ids: string[]) => setDues(ids), []);
  const delega = useQuery({
    queryKey: keys.one(id),
    queryFn: () => apiRequest<F24Detail>(`/treasury/f24/${id}`),
  });
  const data = delega.data;
  usePageTitle(data ? `F24 del ${formatDate(data.dueDate)}` : 'F24');
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    announceTreasuryChange();
  };
  const save = useMutation({
    mutationFn: (body: F24Request) =>
      apiRequest<F24Detail>(`/treasury/f24/${id}`, { method: 'PUT', body }),
    onSuccess: async () => {
      setEditing(false);
      await done();
    },
  });
  const unlink = useMutation({
    mutationFn: () =>
      apiRequest<F24Detail>(`/treasury/f24/${id}/movement`, {
        method: 'PUT',
        body: { movementId: null },
      }),
    onSuccess: done,
  });
  const instalments = useMutation({
    mutationFn: (dueIds: string[]) =>
      apiRequest<F24Detail>(`/treasury/f24/${id}/instalments`, {
        method: 'PUT',
        body: { dueIds },
      }),
    onSuccess: async () => {
      setPaying(false);
      await done();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiRequest<void>(`/treasury/f24/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await done();
      void navigate('/tesoreria/f24');
    },
  });
  if (delega.error) return <p className="error">{delega.error.message}</p>;
  if (!data) return <p className="muted">Caricamento…</p>;
  return (
    <section className="stack">
      <p className="small">
        <Link to={`/tesoreria/f24?anno=${data.dueDate.slice(0, 4)}`}>← F24</Link>
      </p>
      <div className="row between">
        <div>
          <strong>
            Scadenza {formatDate(data.dueDate)} · {formatMoney(data.total)}
          </strong>
          <small className="muted block">
            {F24_ORIGIN_LABELS[data.origin]}
            {data.notes && ` · ${data.notes}`}
          </small>
        </div>
        {canWrite && (
          <div className="row">
            <button type="button" onClick={() => setEditing(true)}>
              Modifica
            </button>
            {!data.movement && data.instalments.length === 0 && (
              <button type="button" className="danger" onClick={() => setDeleting(true)}>
                Elimina
              </button>
            )}
          </div>
        )}
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Sezione</th>
              <th>Codice</th>
              <th>Cosa è</th>
              <th>Riferimento</th>
              <th>Periodo</th>
              <th className="numeric">Debito</th>
              <th className="numeric">Credito</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.id}>
                <td>{F24_SECTION_LABELS[line.section]}</td>
                <td>{line.code}</td>
                <td>
                  {line.description ?? <span className="pill warn">codice nuovo</span>}
                  {line.nature && <small className="muted block">{line.nature}</small>}
                </td>
                <td>{line.reference ?? '—'}</td>
                <td>{line.period ?? '—'}</td>
                <td className="numeric">{formatMoney(line.debit)}</td>
                <td className="numeric">
                  {toCents(line.credit) > 0 ? formatMoney(line.credit) : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card stack">
        <h3>Movimento di banca</h3>
        {data.movement ? (
          <p>
            <Link to={movementPath(data.movement.accountCode, data.movement.id)}>
              {data.movement.accountCode} del {formatDate(data.movement.bookingDate)} ·{' '}
              {formatMoney(data.movement.amount)}
            </Link>
            {canWrite && (
              <button
                type="button"
                className="small"
                disabled={unlink.isPending}
                onClick={() => unlink.mutate()}
              >
                Scollega
              </button>
            )}
          </p>
        ) : (
          <p className="muted small">Si collega dalla lista degli F24, quando l’importo torna.</p>
        )}
        {unlink.error && <p className="error">{unlink.error.message}</p>}
      </div>
      <div className="card stack">
        <h3>Rate pagate con questo F24</h3>
        {data.instalments.length > 0 ? (
          <ul>
            {data.instalments.map((due) => (
              <li key={due.dueId}>
                <Link to={`/tesoreria/rateazioni/${due.planId}`}>
                  Rata {due.number} · {due.planName}
                </Link>{' '}
                · {formatMoney(due.amount)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Nessuna.</p>
        )}
        {canWrite && !paying && (
          <div className="row">
            <button type="button" onClick={() => setPaying(true)}>
              Aggiungi una rata
            </button>
            {data.instalments.length > 0 && (
              <button
                type="button"
                disabled={instalments.isPending}
                onClick={() => instalments.mutate([])}
              >
                Togli le rate
              </button>
            )}
          </div>
        )}
        {paying && (
          <>
            <InstalmentDuesPicker
              movement={{ id }}
              url={`/treasury/f24/${id}/instalment-choices`}
              onChange={chooseDues}
            />
            <div className="actions">
              <button type="button" onClick={() => setPaying(false)}>
                Annulla
              </button>
              <button
                type="button"
                className="primary"
                disabled={dues.length === 0 || instalments.isPending}
                onClick={() =>
                  instalments.mutate([...data.instalments.map((due) => due.dueId), ...dues])
                }
              >
                Salva
              </button>
            </div>
          </>
        )}
        {instalments.error && <p className="error">{instalments.error.message}</p>}
      </div>
      {editing && (
        <F24Form
          title="Modifica F24"
          draft={{
            dueDate: data.dueDate,
            origin: data.origin,
            total: data.total,
            notes: data.notes ?? '',
            lines: data.lines.map(({ section, code, reference, period, debit, credit }) => ({
              section,
              code,
              reference,
              period,
              debit,
              credit,
            })),
          }}
          saving={save.isPending}
          error={save.error?.message}
          onSave={(body) => save.mutate(body)}
          onClose={() => setEditing(false)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title="Eliminare questo F24?"
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate()}
          onCancel={() => setDeleting(false)}
        >
          <p>La delega e le sue righe spariscono. I movimenti di banca non cambiano.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
